import React, { useState } from "react";
import { motion } from "framer-motion";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import UpdateNotificationsForm from "./UpdateNotificationsForm";
import {
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  staggerContainer,
  staggerItem,
  useInView,
} from "../../../utils/animations";

const AppDownloadSection = () => {
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  const downloadLinks = [
    {
      platform: "ios",
      name: "iOS App Store",
      icon: "Apple",
      description: "Coming Soon",
      url: "#ios-coming-soon",
      requirements: "iOS 12.0 or later",
      available: false,
    },
    {
      platform: "android",
      name: "Google Play Store",
      icon: "Smartphone",
      description: "Available Now",
      url: "https://play.google.com/store/apps/details?id=com.tak.business_pos",
      requirements: "Android 6.0 or later",
      available: true,
    },
  ];

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <motion.section
      ref={ref}
      id="app-download"
      className="py-16 lg:py-24 bg-gradient-to-b from-primary/5 to-white"
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={staggerContainer}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-12" variants={staggerItem}>
          <motion.div
            className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6"
            variants={fadeInUp}
          >
            <Icon name="Download" size={16} className="mr-2" />
            Download Now
          </motion.div>

          <motion.h2
            className="text-3xl lg:text-5xl font-bold text-text-primary mb-6"
            variants={fadeInUp}
          >
            Get TAK Business Manager
          </motion.h2>

          <motion.p
            className="text-lg lg:text-xl text-text-secondary max-w-3xl mx-auto mb-8"
            variants={fadeInUp}
          >
            Start managing your business on the go. Download the app and
            experience the easiest way to handle sales, inventory, and business
            analytics.
          </motion.p>
        </motion.div>

        <motion.div
          className="grid lg:grid-cols-2 gap-8 items-center"
          variants={staggerItem}
        >
          {/* Download Options */}
          <motion.div variants={fadeInLeft}>
            <motion.h3
              className="text-2xl font-bold text-text-primary mb-6"
              variants={fadeInUp}
            >
              Download on Android • iOS Coming Soon
            </motion.h3>

            <motion.div className="space-y-4 mb-8" variants={staggerContainer}>
              {downloadLinks.map((link, index) => (
                <motion.div
                  key={link.platform}
                  className={`flex items-center p-6 bg-white rounded-xl border-2 transition-all backdrop-blur-sm ${
                    link.available
                      ? "hover:shadow-lg cursor-pointer border-gray-200 hover:border-primary/50"
                      : "border-gray-200 opacity-75"
                  } ${
                    selectedPlatform === link.platform && link.available
                      ? "border-primary"
                      : ""
                  }`}
                  onClick={() =>
                    link.available && setSelectedPlatform(link.platform)
                  }
                  variants={fadeInUp}
                  whileHover={
                    link.available
                      ? {
                          scale: 1.02,
                          boxShadow: "0 10px 30px -10px rgba(0, 0, 0, 0.3)",
                        }
                      : {}
                  }
                  whileTap={link.available ? { scale: 0.98 } : {}}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                    delay: index * 0.1,
                  }}
                >
                  {link.available ? (
                    <a
                      href={link.url}
                      className="flex items-center w-full"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mr-4">
                        <Icon
                          name={link.icon}
                          size={24}
                          className="text-white"
                        />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-text-primary">
                          {link.name}
                        </h4>
                        <p className="text-sm text-success font-medium">
                          {link.description}
                        </p>
                        <p className="text-xs text-text-secondary mt-1">
                          {link.requirements}
                        </p>
                      </div>
                      <Icon
                        name="ExternalLink"
                        size={20}
                        className="text-gray-400"
                      />
                    </a>
                  ) : (
                    <>
                      <div className="w-12 h-12 bg-gray-300 rounded-lg flex items-center justify-center mr-4">
                        <Icon
                          name={link.icon}
                          size={24}
                          className="text-gray-500"
                        />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-text-primary">
                          {link.name}
                        </h4>
                        <p className="text-sm text-warning font-medium">
                          {link.description}
                        </p>
                        <p className="text-xs text-text-secondary mt-1">
                          {link.requirements}
                        </p>
                      </div>
                      <Icon name="Clock" size={20} className="text-warning" />
                    </>
                  )}
                </motion.div>
              ))}
            </motion.div>

            {/* App Features Preview */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-semibold text-text-primary mb-4">
                What you'll get:
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center">
                  <Icon name="Check" size={16} className="text-success mr-2" />
                  <span className="text-sm text-text-secondary">
                    Free to download
                  </span>
                </div>
                <div className="flex items-center">
                  <Icon name="Check" size={16} className="text-success mr-2" />
                  <span className="text-sm text-text-secondary">
                    Works offline
                  </span>
                </div>
                <div className="flex items-center">
                  <Icon name="Check" size={16} className="text-success mr-2" />
                  <span className="text-sm text-text-secondary">
                    Real-time sync
                  </span>
                </div>
                <div className="flex items-center">
                  <Icon name="Check" size={16} className="text-success mr-2" />
                  <span className="text-sm text-text-secondary">
                    Secure data
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* App Preview */}
          <motion.div className="relative" variants={fadeInRight}>
            <div className="relative mx-auto max-w-sm">
              {/* Phone mockup */}
              <div className="relative bg-gray-900 rounded-[2.5rem] p-2 shadow-2xl">
                <div className="bg-white rounded-[2rem] overflow-hidden">
                  {/* Status Bar */}
                  <div className="bg-gray-900 text-white text-xs px-6 py-2 flex justify-between items-center">
                    <span>9:41</span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Signal" size={12} />
                      <Icon name="Wifi" size={12} />
                      <Icon name="Battery" size={12} />
                    </div>
                  </div>

                  {/* App Store Preview */}
                  <div className="p-4 h-96 bg-gradient-to-b from-gray-50 to-white">
                    {/* App Icon and Info */}
                    <div className="flex items-center mb-6">
                      <img
                        src="/assets/logos/App_Icon.png"
                        alt="TAK Business Manager"
                        className="w-16 h-16 rounded-2xl mr-4"
                      />
                      <div className="flex-1">
                        <h3 className="font-bold text-text-primary">
                          TAK Business Manager
                        </h3>
                        <p className="text-sm text-text-secondary">
                          Business & Productivity
                        </p>
                        <div className="flex items-center mt-1">
                          {[...Array(5)].map((_, i) => (
                            <Icon
                              key={i}
                              name="Star"
                              size={12}
                              className="text-warning fill-current"
                            />
                          ))}
                          <span className="text-xs text-text-secondary ml-2">
                            4.8 • Free
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Download Button */}
                    <button className="w-full bg-primary text-white rounded-full py-3 font-semibold mb-6">
                      GET
                    </button>

                    {/* App Screenshots Preview */}
                    <div className="grid grid-cols-3 gap-2">
                      <img
                        src="/assets/images/mockup-3.png"
                        alt="App Screenshot 1"
                        className="aspect-[9/16] w-full object-cover rounded-lg"
                      />
                      <img
                        src="/assets/images/mockup-4.png"
                        alt="App Screenshot 2"
                        className="aspect-[9/16] w-full object-cover rounded-lg"
                      />
                      <img
                        src="/assets/images/mockup-5.png"
                        alt="App Screenshot 3"
                        className="aspect-[9/16] w-full object-cover rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-success text-white rounded-full p-3 shadow-lg">
                <Icon name="Download" size={20} />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-primary text-white rounded-full p-3 shadow-lg">
                <Icon name="Star" size={20} />
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Enhanced Update Notifications */}
        {/* <UpdateNotificationsForm /> */}
      </div>
    </motion.section>
  );
};

export default AppDownloadSection;
